import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Col, DatePicker, Descriptions, Row, Select, Space, Spin, Statistic, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { useProjectsForSelection } from '@/hooks/useProject';
import {
  getProjectProfitLoss,
  getProjectProfitLossSummary,
  type ProjectProfitLossReport,
  type ProjectProfitLossSummary,
  type ProjectProfitLossSummaryRow,
} from '@/services/financialReports.service';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import BranchFilterSelect from '@/components/finance/report-document/BranchFilterSelect';
import { formatDate, formatMoney, formatPercent, PRINT_PLAIN_AMOUNT_PROFILE } from '@/utils/format';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const SECTION_LABEL_KEYS: Record<string, string> = {
  revenue: 'sectionRevenue',
  direct_cost: 'sectionDirectCost',
  overhead: 'sectionOverhead',
  other_income: 'sectionOtherIncome',
};

function amt(v: number | null | undefined): string {
  return formatMoney(v ?? 0, PRINT_PLAIN_AMOUNT_PROFILE);
}

function pctText(v: number | null | undefined): string {
  return v == null ? '—' : formatPercent(v);
}

function profitStyle(v: number): React.CSSProperties {
  return { color: v < 0 ? '#cf1322' : undefined, fontWeight: 600 };
}

type StatementRow = {
  key: string;
  label: string;
  period: number | null;
  toDate: number | null;
  kind: 'header' | 'account' | 'subtotal' | 'result' | 'margin';
  accountId?: string;
};

function sectionLabel(t: TFunction, key: string, fallback: string): string {
  const labelKey = SECTION_LABEL_KEYS[key];
  return labelKey ? t(labelKey) : fallback;
}

function buildStatementRows(report: ProjectProfitLossReport, t: TFunction): StatementRow[] {
  const rows: StatementRow[] = [];
  const section = (key: string) => report.sections.find((s) => s.key === key)!;
  const pushSection = (key: string) => {
    const s = section(key);
    const label = sectionLabel(t, key, s.label);
    rows.push({ key: `h-${key}`, label, period: null, toDate: null, kind: 'header' });
    for (const a of s.accounts) {
      rows.push({
        key: `a-${a.account_id}`,
        label: `${a.account_code}  ${a.account_name}`,
        period: a.period_amount,
        toDate: a.to_date_amount,
        kind: 'account',
        accountId: a.account_id,
      });
    }
    rows.push({
      key: `t-${key}`,
      label: t('totalSection', { section: label.toLowerCase() }),
      period: s.period_total,
      toDate: s.to_date_total,
      kind: 'subtotal',
    });
  };

  pushSection('revenue');
  pushSection('direct_cost');
  rows.push({ key: 'gp', label: t('grossProfitUpper'), period: report.period.gross_profit, toDate: report.project_to_date.gross_profit, kind: 'result' });
  rows.push({ key: 'gpm', label: t('grossMarginPct'), period: report.period.gross_margin_pct, toDate: report.project_to_date.gross_margin_pct, kind: 'margin' });
  pushSection('overhead');
  pushSection('other_income');
  rows.push({ key: 'np', label: t('netProfitUpper'), period: report.period.net_profit, toDate: report.project_to_date.net_profit, kind: 'result' });
  rows.push({ key: 'npm', label: t('netMarginPct'), period: report.period.net_margin_pct, toDate: report.project_to_date.net_margin_pct, kind: 'margin' });
  return rows;
}

const ProjectProfitLossPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const [searchParams, setSearchParams] = useSearchParams();
  const projectId = searchParams.get('project') || undefined;
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => {
    const from = searchParams.get('from');
    const to = searchParams.get('to');
    return from && to ? [dayjs(from), dayjs(to)] : [dayjs().startOf('year'), dayjs()];
  });
  const [branchId, setBranchId] = useState<string | undefined>(searchParams.get('branch') || undefined);
  const fromDate = range[0].format('YYYY-MM-DD');
  const toDate = range[1].format('YYYY-MM-DD');
  const branchParam = branchId ? { branch_id: branchId } : {};

  const { data: projects = [], isLoading: loadingProjects } = useProjectsForSelection();

  const single = useQuery({
    queryKey: ['fin-project-pl', projectId, fromDate, toDate, branchId],
    queryFn: () => getProjectProfitLoss({ project_id: projectId!, from_date: fromDate, to_date: toDate, ...branchParam }),
    enabled: Boolean(projectId),
  });
  const summary = useQuery({
    queryKey: ['fin-project-pl-summary', fromDate, toDate, branchId],
    queryFn: () => getProjectProfitLossSummary({ from_date: fromDate, to_date: toDate, ...branchParam }),
    enabled: !projectId,
  });
  const docParams = { from: fromDate, to: toDate, ...(projectId ? { project_id: projectId } : {}), ...branchParam };

  const setProject = (id?: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('project', id);
    else next.delete('project');
    setSearchParams(next, { replace: true });
  };

  const active = projectId ? single : summary;
  const errorMessage = (active.error as any)?.response?.data?.message || (active.error as Error | null)?.message;

  const body = projectId ? (
    single.data ? <SingleProjectStatement report={single.data} printMode={false} /> : null
  ) : summary.data ? (
    <SummaryTable report={summary.data} printMode={false} onOpen={setProject} />
  ) : null;

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Row justify="space-between" align="middle" gutter={[12, 12]}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            {t('projectPlShort')}
          </Title>
          <Text type="secondary">{t('projectPlHint')}</Text>
        </Col>
        <Col>
          <ReportDocumentActions reportKey="project-profit-loss" params={docParams} />
        </Col>
      </Row>

      <Card size="small">
        <Space wrap>
          <Select
            showSearch
            allowClear
            style={{ minWidth: 420 }}
            placeholder={t('allProjectsSummary')}
            loading={loadingProjects}
            value={projectId}
            onChange={(v?: string) => setProject(v)}
            optionFilterProp="label"
            options={projects.map((p) => ({
              value: p.id,
              label: `${p.project_number} — ${p.project_name}${p.customer_name ? ` (${p.customer_name})` : ''}`,
            }))}
          />
          <RangePicker
            value={range}
            allowClear={false}
            format="DD MMM YYYY"
            onChange={(v) => v?.[0] && v?.[1] && setRange([v[0], v[1]])}
          />
          <BranchFilterSelect value={branchId} onChange={setBranchId} />
          {projectId ? (
            <Link to={`/fin/reports/project-ledger?project=${projectId}&from=${fromDate}&to=${toDate}&scope=pl`}>
              {t('openProjectLedger')}
            </Link>
          ) : null}
        </Space>
      </Card>

      {active.isFetching && !active.data ? (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spin size="large" />
        </div>
      ) : active.error ? (
        <Alert type="error" showIcon message={t('couldNotLoadProjectPl')} description={errorMessage} />
      ) : (
        <Spin spinning={active.isFetching}>{body}</Spin>
      )}
    </div>
  );
};

const SingleProjectStatement: React.FC<{ report: ProjectProfitLossReport; printMode?: boolean }> = ({ report, printMode }) => {
  const { t } = useTranslation('finance');
  const rows = useMemo(() => buildStatementRows(report, t), [report, t]);
  const { project } = report;
  const ledgerLink = (_accountId?: string) =>
    `/fin/reports/project-ledger?project=${project.project_id}&from=${report.from_date}&to=${report.to_date}&scope=pl`;

  const valueCell = (v: number | null, row: StatementRow) => {
    if (row.kind === 'header' || v == null) return row.kind === 'margin' ? '—' : '';
    if (row.kind === 'margin') return pctText(v);
    const text = amt(v);
    if (row.kind === 'result') return <span style={profitStyle(v)}>{text}</span>;
    if (row.kind === 'subtotal') return <strong>{text}</strong>;
    return text;
  };

  const columns: ColumnsType<StatementRow> = [
    {
      title: t('line'),
      dataIndex: 'label',
      render: (v: string, row) => {
        if (row.kind === 'header') return <strong>{v}</strong>;
        if (row.kind === 'result') return <strong>{v}</strong>;
        if (row.kind === 'subtotal') return <strong style={{ paddingLeft: 12 }}>{v}</strong>;
        if (row.kind === 'margin') return <Text type="secondary">{v}</Text>;
        return (
          <span style={{ paddingLeft: 24 }}>
            {printMode ? v : <Link to={ledgerLink(row.accountId)}>{v}</Link>}
          </span>
        );
      },
    },
    {
      title: t('periodWithDates', { from: formatDate(report.from_date), to: formatDate(report.to_date) }),
      dataIndex: 'period',
      align: 'right',
      width: 220,
      render: valueCell,
    },
    {
      title: t('projectToDateTo', { date: formatDate(report.to_date) }),
      dataIndex: 'toDate',
      align: 'right',
      width: 220,
      render: valueCell,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {printMode ? (
        <Title level={4} style={{ margin: 0 }}>
          {t('projectProfitLoss')}
        </Title>
      ) : null}
      <Descriptions size="small" bordered column={printMode ? 2 : { xs: 1, sm: 2, lg: 3 }}>
        <Descriptions.Item label={t('project')}>
          {project.project_number} — {project.project_name}
        </Descriptions.Item>
        <Descriptions.Item label={t('customer')}>{project.customer_name || '—'}</Descriptions.Item>
        <Descriptions.Item label={t('status')}>{project.project_status || '—'}</Descriptions.Item>
        <Descriptions.Item label={t('contractValue')}>
          {project.contract_value != null ? amt(project.contract_value) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('startDate')}>{project.start_date ? formatDate(project.start_date) : '—'}</Descriptions.Item>
        <Descriptions.Item label={t('currency')}>{report.currency}</Descriptions.Item>
      </Descriptions>

      {!printMode ? (
        <Row gutter={16}>
          <Col xs={24} md={6}>
            <Statistic title={t('revenueToDate')} value={amt(report.project_to_date.revenue)} />
          </Col>
          <Col xs={24} md={6}>
            <Statistic title={t('directCostToDate')} value={amt(report.project_to_date.direct_cost)} />
          </Col>
          <Col xs={24} md={6}>
            <Statistic
              title={t('grossProfitToDate')}
              value={`${amt(report.project_to_date.gross_profit)} (${pctText(report.project_to_date.gross_margin_pct)})`}
              valueStyle={profitStyle(report.project_to_date.gross_profit)}
            />
          </Col>
          <Col xs={24} md={6}>
            <Statistic
              title={t('netProfitToDate')}
              value={`${amt(report.project_to_date.net_profit)} (${pctText(report.project_to_date.net_margin_pct)})`}
              valueStyle={profitStyle(report.project_to_date.net_profit)}
            />
          </Col>
        </Row>
      ) : null}

      <Table
        size="small"
        bordered
        pagination={false}
        rowKey="key"
        dataSource={rows}
        columns={columns}
        rowClassName={(r) => (r.kind === 'result' ? 'ant-table-row-selected' : '')}
      />
      <Text type="secondary" style={{ fontSize: 12 }}>
        {t('projectPlAccountHint')}
      </Text>
    </div>
  );
};

const SummaryTable: React.FC<{
  report: ProjectProfitLossSummary;
  printMode?: boolean;
  onOpen: (projectId: string) => void;
}> = ({ report, printMode, onOpen }) => {
  const { t } = useTranslation('finance');
  const money = (v: number) => <span style={profitStyle(v)}>{amt(v)}</span>;
  const columns: ColumnsType<ProjectProfitLossSummaryRow> = [
    {
      title: t('project'),
      dataIndex: 'project_number',
      width: 140,
      fixed: printMode ? undefined : 'left',
      render: (v: string, r) => (printMode ? v : <a onClick={() => onOpen(r.project_id)}>{v || r.project_id}</a>),
    },
    { title: t('name'), dataIndex: 'project_name', ellipsis: !printMode },
    { title: t('customer'), dataIndex: 'customer_name', ellipsis: !printMode },
    { title: t('revenue'), dataIndex: 'revenue', align: 'right', width: 120, render: (v: number) => amt(v), sorter: (a, b) => a.revenue - b.revenue },
    { title: t('directCost'), dataIndex: 'direct_cost', align: 'right', width: 120, render: (v: number) => amt(v), sorter: (a, b) => a.direct_cost - b.direct_cost },
    { title: t('grossProfit'), dataIndex: 'gross_profit', align: 'right', width: 120, render: money, sorter: (a, b) => a.gross_profit - b.gross_profit },
    { title: t('gpPct'), dataIndex: 'gross_margin_pct', align: 'right', width: 80, render: pctText },
    { title: t('overheads'), dataIndex: 'overhead', align: 'right', width: 110, render: (v: number) => amt(v) },
    { title: t('otherIncome'), dataIndex: 'other_income', align: 'right', width: 110, render: (v: number) => amt(v) },
    { title: t('netProfit'), dataIndex: 'net_profit', align: 'right', width: 120, render: money, sorter: (a, b) => a.net_profit - b.net_profit },
    { title: t('netPct'), dataIndex: 'net_margin_pct', align: 'right', width: 80, render: pctText },
  ];
  const totals = report.totals;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {printMode ? (
        <Title level={4} style={{ margin: 0 }}>
          {t('projectPlSummaryTitle', { from: formatDate(report.from_date), to: formatDate(report.to_date) })}
        </Title>
      ) : (
        <Row gutter={16}>
          <Col xs={24} md={6}>
            <Statistic title={t('projectsWithActivity')} value={report.rows.length} />
          </Col>
          <Col xs={24} md={6}>
            <Statistic title={t('revenue')} value={amt(totals.revenue)} />
          </Col>
          <Col xs={24} md={6}>
            <Statistic title={t('grossProfit')} value={`${amt(totals.gross_profit)} (${pctText(totals.gross_margin_pct)})`} valueStyle={profitStyle(totals.gross_profit)} />
          </Col>
          <Col xs={24} md={6}>
            <Statistic title={t('netProfit')} value={`${amt(totals.net_profit)} (${pctText(totals.net_margin_pct)})`} valueStyle={profitStyle(totals.net_profit)} />
          </Col>
        </Row>
      )}
      <Table
        size="small"
        bordered
        rowKey="project_id"
        dataSource={report.rows}
        columns={columns}
        scroll={printMode ? undefined : { x: 1300 }}
        pagination={printMode ? false : { defaultPageSize: 50, showSizeChanger: true }}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={3}>
              <strong>{t('totalWithCurrency', { currency: report.currency })}</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right"><strong>{amt(totals.revenue)}</strong></Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="right"><strong>{amt(totals.direct_cost)}</strong></Table.Summary.Cell>
            <Table.Summary.Cell index={3} align="right"><strong>{amt(totals.gross_profit)}</strong></Table.Summary.Cell>
            <Table.Summary.Cell index={4} align="right"><strong>{pctText(totals.gross_margin_pct)}</strong></Table.Summary.Cell>
            <Table.Summary.Cell index={5} align="right"><strong>{amt(totals.overhead)}</strong></Table.Summary.Cell>
            <Table.Summary.Cell index={6} align="right"><strong>{amt(totals.other_income)}</strong></Table.Summary.Cell>
            <Table.Summary.Cell index={7} align="right"><strong>{amt(totals.net_profit)}</strong></Table.Summary.Cell>
            <Table.Summary.Cell index={8} align="right"><strong>{pctText(totals.net_margin_pct)}</strong></Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </div>
  );
};

export default ProjectProfitLossPage;
